import { useEffect, useState } from "react";
import { isLocalHostUrl } from "@greg-chick/shared";
import { contentBase } from "../lib/contentApi";

/**
 * **웹에 연결이 안 되면 그렇다고 말해 준다.**
 *
 * ## 왜 필요한가
 * 관리자는 "지금 웹에 무엇이 보이는지" 를 알려 주려고 **웹의 파일을 직접 읽는다**
 * (`/content/asset-manifest.json`, `/art/...`). 그런데 웹이 꺼져 있으면
 * **엣지는 멀쩡한데 그림만 전부 안 나온다.** 화면에는 아무 설명이 없다.
 *
 * 실제로 겪었다(2026-08-11): 엣지·관리자만 켜고 웹을 안 켰더니 "리소스를 불러오기 실패"
 * 만 보였다. 엣지 로그는 85건 전부 200 이라 **엉뚱한 곳을 오래 뒤지게 된다.**
 * 개발자도 그랬으니 운영자는 원인을 알 수가 없다.
 *
 * ## 무엇을 하나
 * 가벼운 파일 하나를 찔러 보고, 안 되면 **무엇을 해야 하는지까지** 띄운다.
 * 판정은 이 한 곳에서만 한다 — 화면마다 "안 보이네요" 를 따로 만들면 갈린다.
 */
type Reach = "checking" | "ok" | "down";

/** 매니페스트는 관리자가 어차피 읽는 파일이라, 이게 되면 나머지도 된다. */
const PROBE = "/content/asset-manifest.json";

export function ContentReach() {
  const [state, setState] = useState<Reach>("checking");
  const base = contentBase();

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        // 5초 안에 답이 없으면 못 쓰는 것으로 본다(꺼진 서버는 보통 즉시 끊긴다)
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), 5000);
        const res = await fetch(`${base}${PROBE}`, { cache: "no-store", signal: ctl.signal });
        clearTimeout(timer);
        if (alive) setState(res.ok ? "ok" : "down");
      } catch {
        // 여기서 조용히 넘어가면 원래 문제로 돌아간다 — 반드시 화면에 띄운다
        if (alive) setState("down");
      }
    };
    void check();
    // 웹을 뒤늦게 켰을 때 저절로 사라지도록 — 운영자가 새로고침을 몰라도 된다
    const id = setInterval(() => void check(), 10_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [base]);

  if (state !== "down") return null;

  const local = isLocalHostUrl(base);
  return (
    <div className="reach" role="status">
      <b className="reach__title">⚠️ 웹에 연결이 안 돼요 — 그림 목록을 불러올 수 없어요</b>
      {local ? (
        <>
          <span className="reach__body">
            웹 서버가 꺼져 있어요. 터미널에서 아래를 실행한 뒤 이 화면이 저절로 사라지는지 보세요.
          </span>
          <code className="reach__cmd">pnpm --filter web dev</code>
        </>
      ) : (
        <span className="reach__body">
          웹({base})이 응답하지 않아요. 배포 상태를 확인해 주세요 — 저장·게시는 계속 됩니다.
        </span>
      )}
      <span className="reach__note">
        엣지(저장·게시)는 따로예요. 이 알림이 떠도 <b>글·설정 저장은 됩니다</b> — 그림만 안 보여요.
      </span>
    </div>
  );
}
